import {
  Mesh,
  PerspectiveCamera,
  PlaneGeometry,
  Raycaster,
  Scene,
  ShadowMaterial,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import { useStore } from "../store/Store";

type props8thPipeline = {
  xr8: any;
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  initialCameraPosition: Vector3;
};

export const three8thPipeline = (
  { xr8, scene, camera, renderer, initialCameraPosition }: props8thPipeline,
  setTrackingReady: (ready: boolean) => void
) => {
  let placementMode = useStore.getState().tapToPlace;
  const raycaster = new Raycaster();
  const rayOrigin = new Vector2(0, 0);
  let groundLocation = new Vector3(0, 0, 0);

  const surface = new Mesh(
    new PlaneGeometry(10000, 10000, 1, 1),
    new ShadowMaterial({ opacity: 0.3 })
  );
  surface.name = "surface";
  surface.rotateX(-Math.PI / 2);
  surface.position.set(0, 0, 0);
  surface.receiveShadow = true;

  // Might be needed if we want to have camera-space objects which are not tracked
  // let trackingGroup: THREE.Scene;
  if (placementMode) {
    scene.add(surface);

    // scene.traverse((o: Object3D) => {
    //   if (o.name === "trackingGroup") {
    //     trackingGroup = o as THREE.Scene;
    //   }
    // });
    const unsub2 = useStore.subscribe(
      (store) => store.tapToPlace,
      (mode: boolean, prevMode: boolean) => {
        placementMode = mode;
        if (prevMode === true) {
          scene.remove(surface);
          unsub2();
        }
      }
    );
  }

  let trackingDone = false;
  return {
    name: "surfacePipeline",
    onException: (err: any) => console.error(err),
    onCanvasSizeChange: ({
      canvasWidth,
      canvasHeight,
    }: {
      canvasWidth: number;
      canvasHeight: number;
    }) => {
      if (
        renderer.domElement.width !== canvasWidth &&
        renderer.domElement.height !== canvasHeight
      ) {
        console.log("onCanvasSizeChange", canvasWidth, canvasHeight);
        renderer?.setSize(canvasWidth, canvasHeight);
        camera.aspect = canvasWidth / canvasHeight;
        camera.updateProjectionMatrix();
      }
    },
    onStart: ({
      canvasWidth,
      canvasHeight,
    }: {
      canvasWidth: number;
      canvasHeight: number;
    }) => {
      renderer.autoClearColor = false;

      if (
        renderer.domElement.width !== canvasWidth &&
        renderer.domElement.height !== canvasHeight
      ) {
        renderer.setSize(canvasWidth, canvasHeight);
        camera.aspect = canvasWidth / canvasHeight;
        camera.updateProjectionMatrix();
      }

      if (initialCameraPosition) {
        camera.position.copy(initialCameraPosition);
      }

      xr8!.XrController.updateCameraProjectionMatrix({
        origin: camera.position,
        facing: {
          x: camera.quaternion.x,
          y: camera.quaternion.y,
          z: camera.quaternion.z,
          w: camera.quaternion.w,
        },
      });
    },
    onCameraStatusChange: ({ status }: any) => {
      console.log("has video", status);
    },
    onUpdate: ({ processCpuResult, fps }: any) => {
      // update camera position
      //   console.log('onUpdate', processCpuResult)
      const reality = processCpuResult?.reality;

      if (reality) {
        const { rotation, position, intrinsics } = reality;

        for (let i = 0; i < 16; i += 1) {
          camera.projectionMatrix.elements[i] = intrinsics[i];
        }
        camera.projectionMatrixInverse.copy(camera.projectionMatrix);
        camera.projectionMatrixInverse.invert();
        if (rotation) {
          camera.setRotationFromQuaternion(rotation as any);
        }

        if (position) {
          camera.position.copy(position as any);
        }

        camera.matrixWorldNeedsUpdate = true;

        if (placementMode) {
          raycaster.setFromCamera(rayOrigin, camera);
          const intersects = raycaster.intersectObject(surface, true);
          if (intersects.length > 0) {
            const [intersect] = intersects;
            groundLocation = intersect.point;
          }
          scene.position.y = 0.1;
          scene.position.lerp(groundLocation, 0.4);
          scene.rotation.y = camera.rotation.y;
        }
      }

      // update tracking flag
      if (
        !trackingDone &&
        (processCpuResult?.reality?.trackingStatus === "NORMAL" ||
          processCpuResult?.reality?.trackingStatus === "LIMITED")
      ) {
        trackingDone = true;
        setTrackingReady(true);
      }
    },
    onRender: () => {
      if (!renderer) {
        return;
      }

      if (scene) {
        renderer.render(scene, camera);
      }
    },
  };
};
